import * as moment from 'moment'
import { HttpResult, none, some } from 'widgets-for-react'
import { getCsrfToken } from '../../../utils/csrf'
import { prefixUrlWithLocale } from '../../../utils/url'
import { AppPayment, Booking } from './bookingDetailsState'

type PaymentsOverrides = {
  due_date: string
}

type BookingOverrides = {
  arrival_date: string
  departure_date: string
  replacement_booking: string
  arrival_date_history: Array<{
    from?: string
    to: string
    updated_at: string
  }>
  payments: Array<Omit<AppPayment, keyof PaymentsOverrides> & PaymentsOverrides>
}

type BookingApiResponse = Omit<Booking, keyof BookingOverrides> &
  BookingOverrides

const bookingResponseParser = (x: BookingApiResponse): Booking => ({
  ...x,
  arrival_date: moment(x.arrival_date),
  departure_date: moment(x.departure_date),
  replacement_booking: !!x.replacement_booking
    ? some(x.replacement_booking)
    : none(),
  arrival_date_history: x.arrival_date_history.map(x => ({
    from: !!x.from ? moment(x.from) : null,
    to: !!x.to ? moment(x.to) : null,
    updated_at: !!x.updated_at ? moment(x.updated_at) : null
  })),
  payments: x.payments.map(x => ({
    ...x,
    due_date: moment(x.due_date)
  }))
})

export async function getBooking(
  identifier: string
): Promise<HttpResult<Booking>> {
  const res = await fetch(
    prefixUrlWithLocale(`/api/superadmin/bookings/${identifier}`)
  )
  if (!res.ok) {
    return { kind: 'failed', status: res.status }
  }
  const ret = (await res.json()) as BookingApiResponse

  return {
    kind: 'result',
    value: bookingResponseParser(ret),
    status: 200
  }
}

export type Transaction = {
  livemode: boolean
  uid: string
  object: string
  created: number
  updated: number
  completed: number
  merchant_uid: string
  has_checkout: boolean
  payment_method: string
  payment_flow: string
  amount: number
  return_url: string
  redirect_url: string
  notify_url: string
  status: string
  statuses: Array<{
    uid: string
    object: string
    created: number
    updated: number
    status: string
  }>
  escrow: {
    uid: string
    object: string
    period: number
    start: number
    end: number
  }
  fees: {
    merchant_gateway_fee: number
    merchant_transaction_fee: number
    merchant_order_fee: number
    merchant_payable_amount: number
    gateway_fee: number
    transaction_fee: number
    order_fee: number
    payable_amount: number
  }
  refunds: Array<{
    uid: string
    object: string
    created: number
    updated: number
    paid: number
    amount: number
    status: string
    message: string
    internal_reason: string
  }>
}

type TranchantPayment = {
  Id: number
  MerchantReference: string
  Amount: number
  CreatedAt: Date
  UpdatedAt: Date
  BookingId: number
  Term: number
  DueDate: Date
  Paid: boolean
  PaidAt: Date
  TermsAcceptedAt?: Date
  TermsAccepted?: boolean
  TenantInvoiceId?: string
  Identifier: string
  OppTransactionUid: string
}

export type TransactionRefundingSpecification = {
  uid: string
  refundable_amount: number
  has_escrow: boolean
  escrow_ends_on?: string
  can_remove_escrow?: boolean
  escrow_expired?: boolean
  amount: number
  refunded_amount: number
  transaction: Transaction
  payment: TranchantPayment
}

export type RefundingSpecification = {
  refundable_amount: number
  settlement_amount: number
  in_escrow_amount: number
  expired_escrow_amount: number
  transactions: TransactionRefundingSpecification[]
}

export async function getRefundingSpecification(
  identifier: string
): Promise<HttpResult<RefundingSpecification>> {
  const res = await fetch(
    prefixUrlWithLocale(
      `api/opp/superadmin/bookings/${identifier}/refunding_specification`
    )
  )
  if (!res.ok) {
    return { kind: 'failed', status: res.status }
  }

  return {
    kind: 'result',
    value: await res.json(),
    status: 200
  }
}

export type PostRefundResponse =
  | {
      kind: 'error'
      value: string
    }
  | {
      kind: 'success'
    }

export async function postRefund(
  identifier: string,
  amount: number
): Promise<HttpResult<PostRefundResponse>> {
  const res = await fetch(
    prefixUrlWithLocale(`api/opp/superadmin/bookings/${identifier}/refund`),
    {
      method: 'post',
      body: JSON.stringify({
        amount
      }),
      headers: [
        ['Content-Type', 'application/json'],
        ['Accept', 'application/json'],
        ['X-CSRF-Token', getCsrfToken()]
      ]
    }
  )

  if (!res.ok) {
    if (res.status !== 422) {
      return { kind: 'failed', status: res.status }
    }
    const ret = await res.json()
    return {
      kind: 'result',
      value: {
        kind: 'error',
        value: !!ret.error ? ret.error.message : JSON.stringify(ret)
      },
      status: 200
    }
  }

  return {
    kind: 'result',
    value: { kind: 'success' },
    status: 200
  }
}

export type RemoveEscrowResponse = {}

export async function removeEscrow(
  transaction: Transaction
): Promise<HttpResult<RemoveEscrowResponse>> {
  const res = await fetch(
    prefixUrlWithLocale(
      `api/opp/superadmin/transactions/${transaction.uid}/remove_escrow`
    ),
    {
      method: 'post',
      headers: [
        ['Content-Type', 'application/json'],
        ['Accept', 'application/json'],
        ['X-CSRF-Token', getCsrfToken()]
      ]
    }
  )

  if (!res.ok) {
    alert(I18n.t('superadmin.bookings.refunding.remove_escrow_error'))
    return { kind: 'failed', status: res.status }
  }

  return {
    kind: 'result',
    value: {},
    status: 200
  }
}
